import { faBarChart } from '@fortawesome/pro-regular-svg-icons/faBarChart';
import { faSchool } from '@fortawesome/pro-regular-svg-icons/faSchool';
import useApi from '@propertypal/shared/src/hooks/useApi';
import { MarketTrendsResponse } from '@propertypal/shared/src/types/marketTrends';
import { Property } from '@propertypal/shared/src/types/property';
import { SchoolsResult } from '@propertypal/shared/src/types/schools';
import { roundOff } from '@propertypal/shared/src/utils/number';
import { getFormattedPrice } from '@propertypal/shared/src/utils/property';
import getAveragePriceLabel from '@propertypal/shared/src/utils/property/marketTrends';
import { getUrl } from '@propertypal/shared/src/utils/schools';
import dynamic from 'next/dynamic';
import React, { FunctionComponent, useEffect } from 'react';
import { PulseLoader } from 'react-spinners';
import { useTheme } from 'styled-components';
import WhiteButton from '../buttons/WhiteButton';
import { InfoBox, InfoRow } from '../layout/InfoBox';
import { Text } from '../typography';
import { MapWrapper, Row } from './PropertyLocation.style';

interface Props {
  property: Property;
  updateSectionUrl: (url: string) => void;
  fetchMarketTrendsData: () => void;
  marketTrendsData?: MarketTrendsResponse;
  marketTrendsLoading?: boolean;
}

const LeafletMap = dynamic(() => import('@propertypal/web-ui/src/map/LeafletMap'), { ssr: false });

const PropertyLocation: FunctionComponent<Props> = (props) => {
  const [schoolData, , , fetchSchoolData] = useApi<SchoolsResult>(getUrl(props.property, null, null), false);
  const isDevelopment = props.property.type === 'development' || props.property.development;
  const schools = schoolData?.results ? Object.values(schoolData.results).flat() : [];
  const primary = schools.filter((s) => s.phase === 'PRIMARY');
  const secondary = schools.filter((s) => s.phase === 'SECONDARY');
  const theme = useTheme();

  useEffect(() => {
    fetchSchoolData();

    if (!isDevelopment) {
      props.fetchMarketTrendsData();
    }
  }, []);

  if (!props.property.coordinate) {
    return null;
  }

  return (
    <InfoBox title="Location" containerStyle={{ margin: '30px 0' }}>
      <MapWrapper>
        <LeafletMap
          lat={props.property.coordinate.latitude}
          lng={props.property.coordinate.longitude}
          zoom={props.property.coordinate.mapZoomLevel}
          onExpand={() => props.updateSectionUrl('/map')}
        />
      </MapWrapper>

      <Row>
        <InfoBox title={`${schools.length} Nearest Schools`} icon={faSchool}>
          {!!secondary.length && (
            <InfoRow title="Secondary">
              {secondary.map((school) => (
                <p key={school.id} data-testid="school-row">
                  {school.name} <b style={{ display: 'inline-block' }}>({school.distanceText})</b>
                </p>
              ))}
            </InfoRow>
          )}

          {!!primary.length && (
            <InfoRow title="Primary">
              {primary.map((school) => (
                <p key={school.id} data-testid="school-row">
                  {school.name} <b style={{ display: 'inline-block' }}>({school.distanceText})</b>
                </p>
              ))}
            </InfoRow>
          )}

          {schoolData && (
            <WhiteButton
              containerStyle={{ height: 49, margin: '15px 0' }}
              onClick={() => props.updateSectionUrl('/schools')}
            >
              View more nearby schools
            </WhiteButton>
          )}

          {!schoolData && <PulseLoader style={{ display: 'block', margin: '30px auto', width: 60 }} />}
        </InfoBox>

        {!isDevelopment && props.property.saleType.key === 'sale' && (
          <InfoBox title="Local House Prices" icon={faBarChart}>
            <div>
              {!props.marketTrendsLoading && !props.marketTrendsData && (
                <Text align="center" mt={30}>
                  No data available
                </Text>
              )}

              {props.marketTrendsLoading && (
                <PulseLoader color={theme.textDark} style={{ display: 'block', margin: '30px auto', width: 60 }} />
              )}

              {props.marketTrendsData?.averageAreaPrice && (
                <InfoRow title={getAveragePriceLabel(props.marketTrendsData.averageAreaPrice, { lineBreak: true })}>
                  {getFormattedPrice(roundOff(props.marketTrendsData?.averageAreaPrice?.value || 0, 0), '£')}
                </InfoRow>
              )}

              {props.marketTrendsData?.averageAreaPriceGrowth?.change && (
                <InfoRow
                  title={getAveragePriceLabel(props.marketTrendsData.averageAreaPriceGrowth, { lineBreak: true })}
                >
                  {roundOff(props.marketTrendsData.averageAreaPriceGrowth.change * 100, 1)}%
                </InfoRow>
              )}
            </div>

            {props.marketTrendsData && (
              <WhiteButton
                containerStyle={{ height: 49, margin: '15px 0' }}
                onClick={() => props.updateSectionUrl('/market-trends')}
              >
                View more local property prices
              </WhiteButton>
            )}
          </InfoBox>
        )}
      </Row>
    </InfoBox>
  );
};

export default PropertyLocation;
